const EMAIL = "knotforgottenapp@gmail.com";

const mail = (
  <a
    href={`mailto:${EMAIL}`}
    data-testid="legal-email-link"
    className="font-semibold text-[#1D8A99] underline decoration-[#1D8A99]/40 underline-offset-4 transition-colors hover:text-[#0B4B52]"
  >
    {EMAIL}
  </a>
);

export const PRIVACY_SECTIONS = [
  {
    id: "information-we-collect",
    title: "Information We Collect",
    body: (
      <>
        <p>The information we collect depends on how you use the App and the features you choose to use.</p>
        <p><strong className="text-[#0A192F]">Information you provide</strong></p>
        <ul>
          <li><strong className="text-[#0A192F]">Account information:</strong> your name and email address. Your password is stored only as a secure cryptographic hash — we never store your plain-text password. If you sign in with Apple or Google, we receive the identifier and profile details that provider shares with us.</li>
          <li><strong className="text-[#0A192F]">Boat information:</strong> boat names, make, model, year, specifications, engine details (manufacturer, model, serial number, engine hours), and configuration choices (propulsion, systems, electronics, storage) that you choose to enter.</li>
          <li><strong className="text-[#0A192F]">Maintenance and service information:</strong> maintenance and repair entries, service history, due dates, reminders, notes, costs, vendor details, and trip logs (including dates, hours run, and fuel amounts and costs you record).</li>
          <li><strong className="text-[#0A192F]">Photos and documents</strong> that you voluntarily upload, such as receipts, registrations, warranties, and boat photos.</li>
          <li><strong className="text-[#0A192F]">Support communications:</strong> the messages you send us and your reply email address.</li>
        </ul>
        <p><strong className="text-[#0A192F]">Information collected automatically</strong></p>
        <p>
          The App does not include third-party analytics, advertising, or cross-app tracking SDKs. Like most
          online services, our servers transiently process limited technical information — such as your IP
          address — to deliver the service, enforce security rate limits, and prevent abuse.
        </p>
        <p><strong className="text-[#0A192F]">Location information</strong></p>
        <p>
          Knot Forgotten does not access your device's GPS or location services. The only location-related
          information involved is the home-port or city name you choose to type in for weather forecasts;
          that text is sent to the Open-Meteo geocoding service solely to retrieve weather data.
        </p>
      </>
    ),
  },
  {
    id: "how-we-use",
    title: "How We Use Information",
    body: (
      <ul>
        <li>Providing and operating Knot Forgotten.</li>
        <li>Storing and displaying your boat information, maintenance records, service history, trips, receipts, documents, and photos.</li>
        <li>Generating smart maintenance schedules based on your boat's configuration and providing the reminders you enable.</li>
        <li>Processing receipt and document scans you request (AI-assisted field extraction).</li>
        <li>Retrieving weather forecasts for the home port you enter.</li>
        <li>Managing your account, subscription, and entitlements.</li>
        <li>Responding to support requests.</li>
        <li>Troubleshooting and improving the App, maintaining security, and preventing misuse.</li>
        <li>Complying with applicable legal requirements.</li>
      </ul>
    ),
  },
  {
    id: "how-we-share",
    title: "How We Share Information",
    body: (
      <>
        <p>We do not sell your personal information, and we do not share it with advertising networks. We share information only with the service providers needed to operate the App:</p>
        <ul>
          <li><strong className="text-[#0A192F]">Cloud database and hosting providers</strong> — store your account information and records so they are available to you across sessions and devices.</li>
          <li><strong className="text-[#0A192F]">Cloud object-storage provider</strong> — stores photos and documents you upload. Files are addressed by opaque, non-guessable identifiers and served through short-lived signed URLs.</li>
          <li><strong className="text-[#0A192F]">Apple (App Store / StoreKit and Sign in with Apple)</strong> — processes in-app purchases and, if you choose it, authenticates your sign-in.</li>
          <li><strong className="text-[#0A192F]">Google</strong> — authenticates your sign-in if you choose to sign in with Google.</li>
          <li><strong className="text-[#0A192F]">RevenueCat</strong> — manages subscription entitlements. RevenueCat processes an app user identifier and purchase receipt information from the App Store to confirm your subscription status; it does not receive your payment card details.</li>
          <li><strong className="text-[#0A192F]">OpenAI</strong> — when you choose to scan a receipt or document, the image is processed by OpenAI's vision models to extract structured details (vendor, date, totals, line items). The image is sent solely to fulfill that scan request.</li>
          <li><strong className="text-[#0A192F]">Open-Meteo</strong> — receives only the home-port or city text you type in, to return weather and marine forecasts. No personal information is sent with these requests.</li>
          <li><strong className="text-[#0A192F]">Stripe</strong> — if you purchase a subscription through a web checkout where offered, Stripe processes the payment; we receive only the resulting subscription status.</li>
        </ul>
        <p>
          We may also disclose information if required by law or valid legal process. Read-only share links
          you create yourself are visible to anyone who has the link until you revoke them.
        </p>
      </>
    ),
  },
  {
    id: "subscriptions-payments",
    title: "Subscriptions and Payments",
    body: (
      <>
        <p>Knot Forgotten offers optional paid subscription features (Knot Forgotten Plus).</p>
        <p>
          If you purchase a subscription in the iOS app, the transaction is processed by Apple through the
          App Store's in-app purchase system. Knot Forgotten does not receive your complete payment card
          information from Apple. RevenueCat is used to manage subscription entitlements and processes the
          information necessary to confirm and maintain your subscription status.
        </p>
        <p>
          New accounts include a 30-day Knot Forgotten Plus trial; no payment method is required to start it.
          If you do not subscribe, the account simply returns to the free tier when the trial ends.
        </p>
      </>
    ),
  },
  {
    id: "data-storage-security",
    title: "Data Storage and Security",
    body: (
      <p>
        We use reasonable administrative and technical measures to protect your information — including
        hashed password storage, authenticated access controls, and short-lived signed URLs for uploaded
        files. That said, no method of electronic storage or transmission can be guaranteed to be completely
        secure, and we cannot promise absolute security.
      </p>
    ),
  },
  {
    id: "data-retention",
    title: "Data Retention",
    body: (
      <p>
        We retain your information while your account remains active and for as long as reasonably necessary
        for legitimate business, legal, security, or operational purposes. When you delete your account in
        the App, your boats, records, and uploaded files are permanently deleted from our systems.
      </p>
    ),
  },
  {
    id: "your-rights",
    title: "Your Privacy Rights",
    body: (
      <p>
        Depending on where you live, you may have rights to access, correct, delete, or receive a copy of
        your personal information, subject to applicable law. You can view and manage your records directly
        in the App at any time, export a boat history report, or delete your account entirely. For any
        privacy request, contact us at {mail}.
      </p>
    ),
  },
  {
    id: "account-deletion",
    title: "Account and Data Deletion",
    body: (
      <p>
        You can permanently delete your account at any time inside the App: open{" "}
        <strong className="text-[#0A192F]">Profile → Danger Zone → Delete my account</strong>. This erases
        your account, boats, maintenance records, documents, photos, and uploaded files. This action is
        irreversible. You can also request deletion by emailing {mail}.
      </p>
    ),
  },
  {
    id: "notifications-permissions",
    title: "Notifications and Permissions",
    body: (
      <>
        <p>
          Knot Forgotten uses only on-device (local) notifications to remind you before maintenance is due
          and before documents, warranties, or registrations expire. There is no push-notification server,
          and no notification data leaves your device beyond scheduling the reminder itself.
        </p>
        <p>The App requests the following device permissions, only when you use the related feature:</p>
        <ul>
          <li><strong className="text-[#0A192F]">Notifications</strong> — to schedule local maintenance and expiration reminders.</li>
          <li><strong className="text-[#0A192F]">Camera</strong> — to take photos of receipts, damage, or your boat for your records.</li>
          <li><strong className="text-[#0A192F]">Photo Library</strong> — to attach existing photos to your records and to save exported boat history reports.</li>
          <li><strong className="text-[#0A192F]">Face ID</strong> — optionally, to keep your saved sign-in secure.</li>
        </ul>
        <p>The App does not access your contacts, precise location, Bluetooth, or microphone.</p>
      </>
    ),
  },
  {
    id: "third-party-services",
    title: "Third-Party Services and Links",
    body: (
      <p>
        The App relies on the third-party services listed in the "How We Share Information" section to
        provide its functionality. Those providers operate under their own privacy policies, which we
        encourage you to review. Links you create through the App's read-only sharing feature can be viewed
        by anyone who has the link until you revoke it.
      </p>
    ),
  },
  {
    id: "childrens-privacy",
    title: "Children's Privacy",
    body: (
      <p>
        Knot Forgotten is not directed toward children under 13, and we do not knowingly collect personal
        information from children under 13. If you believe a child has provided us personal information,
        contact us at {mail} and we will delete it.
      </p>
    ),
  },
  {
    id: "international-transfers",
    title: "International Data Transfers",
    body: (
      <p>
        Your information is stored and processed on managed cloud infrastructure located in the United
        States. If you use the App from outside the United States, you understand that your information will
        be transferred to and processed in the United States, where data-protection laws may differ from
        those in your jurisdiction.
      </p>
    ),
  },
  {
    id: "state-privacy-rights",
    title: "State Privacy Rights",
    body: (
      <p>
        Residents of certain U.S. states (including California, Virginia, Colorado, Connecticut, and others)
        may have additional rights under applicable state privacy laws — such as the right to know, access,
        correct, delete, or opt out of certain processing of personal information. Knot Forgotten does not
        sell personal information and does not share it for cross-context behavioral advertising. To
        exercise any applicable right, contact us at {mail}.
      </p>
    ),
  },
  {
    id: "changes",
    title: "Changes to This Privacy Policy",
    body: (
      <p>
        We may update this Privacy Policy periodically. When we make changes, we will revise the "Last
        Updated" date above, and for material changes we will make reasonable efforts to notify you.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Knot Forgotten
        <br />
        Email: {mail}
      </p>
    ),
  },
];

export const TERMS_SECTIONS = [
  {
    id: "use-of-app",
    title: "Use of the App",
    body: (
      <>
        <p>
          Knot Forgotten is a boat management and record-keeping application intended to help users organize
          information about their boats, maintenance, service history, reminders, and related records.
        </p>
        <p>You agree to use the App only for lawful purposes and in accordance with these Terms.</p>
      </>
    ),
  },
  {
    id: "your-account",
    title: "Your Account",
    body: (
      <p>
        The App requires an account. You are responsible for maintaining the confidentiality of your account
        credentials and for activity occurring through your account. You agree to provide accurate
        information and keep your account information reasonably current.
      </p>
    ),
  },
  {
    id: "user-content",
    title: "User Content",
    body: (
      <>
        <p>
          You may enter, upload, or otherwise provide information through Knot Forgotten, including boat
          information, maintenance records, notes, photographs, and other content supported by the App
          ("User Content").
        </p>
        <ul>
          <li>You retain ownership of your User Content.</li>
          <li>You grant Knot Forgotten the limited rights necessary to store, process, display, and transmit your User Content solely to provide and operate the Services.</li>
          <li>You are responsible for the accuracy and legality of the information you enter or upload.</li>
        </ul>
      </>
    ),
  },
  {
    id: "boat-information",
    title: "Boat and Maintenance Information",
    body: (
      <>
        <p>Knot Forgotten is an organizational and record-keeping tool.</p>
        <p>
          Information, reminders, maintenance schedules, or other content provided by the App — including
          smart maintenance suggestions generated from manufacturer-style intervals — should not be treated
          as professional mechanical, engineering, safety, navigation, legal, or other professional advice.
        </p>
        <p>
          You are responsible for determining when your boat requires inspection, maintenance, repair, or
          professional service.
        </p>
      </>
    ),
  },
  {
    id: "subscriptions",
    title: "Subscriptions",
    body: (
      <>
        <p>Knot Forgotten offers subscription-based features (Knot Forgotten Plus).</p>
        <p>
          If you purchase a subscription through Apple's App Store, the purchase is processed through
          Apple's in-app purchase system and is subject to Apple's applicable terms and policies.
          Subscription pricing, renewal, cancellation, and refund terms are governed by the applicable App
          Store rules and the terms presented at the time of purchase.
        </p>
        <p>
          RevenueCat is used to manage subscription entitlements and may process information necessary to
          provide subscription functionality, such as an app user identifier and purchase receipt data.
        </p>
        <p>
          You can manage or cancel a subscription purchased through Apple in your device's Settings under
          your Apple ID → Subscriptions.
        </p>
      </>
    ),
  },
  {
    id: "free-trials",
    title: "Free Trials",
    body: (
      <p>
        New Knot Forgotten accounts currently include a 30-day Knot Forgotten Plus trial. No payment method
        is required to begin the trial, and it does not automatically convert into a paid subscription: when
        the trial ends, the account returns to the free tier unless you choose to subscribe. If a different
        trial is ever offered through the App Store, its terms, duration, eligibility, and renewal conditions
        will be presented to you before you begin, and it will be governed by Apple's subscription rules.
      </p>
    ),
  },
  {
    id: "acceptable-use",
    title: "Acceptable Use",
    body: (
      <>
        <p>You may not:</p>
        <ul>
          <li>Use the App for unlawful purposes.</li>
          <li>Attempt to gain unauthorized access to the App or its systems.</li>
          <li>Interfere with the operation or security of the Services.</li>
          <li>Upload malicious code or content.</li>
          <li>Abuse, reverse engineer, or attempt to circumvent technical protections except where expressly permitted by applicable law.</li>
          <li>Use the Services to infringe another person's rights.</li>
        </ul>
      </>
    ),
  },
  {
    id: "intellectual-property",
    title: "Intellectual Property",
    body: (
      <p>
        The App, including its software, design, branding, logos, text, graphics, and other original
        materials, is owned by or licensed to Knot Forgotten and is protected by applicable intellectual
        property laws. Except as expressly permitted by these Terms, you may not reproduce, distribute,
        modify, or create derivative works from the App or its proprietary materials.
      </p>
    ),
  },
  {
    id: "availability-changes",
    title: "Availability and Changes",
    body: (
      <p>
        We may modify, suspend, or discontinue features of the Services from time to time. We do not
        guarantee that the Services will always be available, uninterrupted, or error-free.
      </p>
    ),
  },
  {
    id: "disclaimer",
    title: "Disclaimer",
    body: (
      <>
        <p>The Services are provided for organizational and informational purposes.</p>
        <p>
          To the maximum extent permitted by applicable law, Knot Forgotten makes no warranties regarding
          the accuracy, completeness, reliability, or availability of information provided through the
          Services.
        </p>
        <p>
          Knot Forgotten does not provide professional mechanical, engineering, navigation, legal,
          insurance, or safety advice.
        </p>
      </>
    ),
  },
  {
    id: "limitation-liability",
    title: "Limitation of Liability",
    body: (
      <p>
        To the maximum extent permitted by applicable law, Knot Forgotten and its owners, operators,
        employees, contractors, and service providers will not be liable for indirect, incidental, special,
        consequential, or punitive damages arising from or related to your use of the Services. Nothing in
        these Terms excludes or limits liability that cannot legally be excluded or limited.
      </p>
    ),
  },
  {
    id: "indemnification",
    title: "Indemnification",
    body: (
      <p>
        To the extent permitted by applicable law, you agree to indemnify and hold harmless Knot Forgotten
        and its owners, operators, employees, contractors, and service providers from claims, damages,
        liabilities, and expenses arising from your misuse of the Services, violation of these Terms, or
        infringement of another person's rights.
      </p>
    ),
  },
  {
    id: "termination",
    title: "Termination",
    body: (
      <>
        <p>
          We may suspend or terminate access to the Services if you violate these Terms or where reasonably
          necessary to protect the Services, users, or others.
        </p>
        <p>
          You may stop using the Services at any time. You can delete your account and all associated data
          from within the App (Profile → Danger Zone → Delete my account), as described in our Privacy
          Policy.
        </p>
      </>
    ),
  },
  {
    id: "changes-to-terms",
    title: "Changes to These Terms",
    body: (
      <p>
        We may update these Terms from time to time. When changes are made, we will update the Last Updated
        date. Your continued use of the Services after an updated version becomes effective constitutes
        acceptance of the updated Terms to the extent permitted by applicable law.
      </p>
    ),
  },
  {
    id: "governing-law",
    title: "Governing Law",
    body: (
      <p>
        These Terms and your use of the Services are governed by and construed in accordance with the laws
        of the State of Florida, without regard to its conflict-of-laws principles. Any dispute arising out
        of or relating to these Terms or the Services will be subject to the exclusive jurisdiction of the
        state and federal courts located in the State of Florida, and you consent to the personal
        jurisdiction of those courts.
      </p>
    ),
  },
  {
    id: "contact",
    title: "Contact",
    body: (
      <p>
        Knot Forgotten
        <br />
        Email: {mail}
      </p>
    ),
  },
];

export const SUPPORT_EMAIL = EMAIL;
